import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { DeviceCalibrationHistory } from "src/app/models/deviceCalibrationHistory.model";
import { CalibrationEquipmentService } from "src/app/services/calibration-equipment.service";
import { DeviceCalibrationHistoryService } from "src/app/services/deviceCalibrationHistory.service";
import { ToastService } from "src/app/services/toast.service";
import { calibrationDetailsRouteConfig } from "src/app/shared/constants/calibrationDetailsRouteConfig.constants";
import nameOf from "src/app/shared/form/nameOf.helper";
import { openFileInNewTab } from "src/app/shared/utils/file-link.util";

@Component({
  standalone: false,
    selector: "app-calibration-maintenance-history",
    templateUrl: "./calibration-maintenance-history.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        "./calibration-maintenance-history.component.scss",
        "./../../shared/grid/core-grid/core-grid.component.scss",
    ],
})
export class CalibrationMaintenanceHistoryComponent implements OnInit {
    tabs: { routerLink: string; title: string }[];
    displayedColumns: string[] = [
        nameOf<DeviceCalibrationHistory>((x) => x.id),
        nameOf<DeviceCalibrationHistory>((x) => x.calibrationDate),
        nameOf<DeviceCalibrationHistory>((x) => x.nextCalibrationDate),
        nameOf<DeviceCalibrationHistory>((x) => x.calibrationStatus),
        nameOf<DeviceCalibrationHistory>((x) => x.certificateUrl),
    ];
    dataSource = new MatTableDataSource<DeviceCalibrationHistory>();
    defaultOrderBy: "asc" | "desc" = "asc";
    sortActive: string = "id";
    calibrationId: string;
    totalItems: number;
    pageIndex: number = 0;
    pageSizeOptions = [10, 20, 50, 100];
    pageSize: number = 20;
    isLoading = false;
    equipmentIdentifier: { label: string; value: string } | null = null;

    constructor(
        private activatedRoute: ActivatedRoute,
        private deviceCalibrationHistoryService: DeviceCalibrationHistoryService,
        private calibrationEquipmentService: CalibrationEquipmentService,
        private toastService: ToastService,
    ) {}

    ngOnInit(): void {
        this.calibrationId = this.activatedRoute.snapshot.params.id;
        this.tabs = calibrationDetailsRouteConfig(this.calibrationId);
        this.getEquipmentIdentifier();
        this.getCalibrationHistory();
    }

    // Every row belongs to the same equipment, so it is named once in the header instead of per row.
    getEquipmentIdentifier() {
        this.calibrationEquipmentService.getById(this.calibrationId).subscribe({
            next: (equipment) => {
                if (equipment?.equipmentCode) {
                    this.equipmentIdentifier = { label: "Equipment Code", value: equipment.equipmentCode };
                } else if (equipment?.serialNumber) {
                    this.equipmentIdentifier = { label: "Serial Number", value: equipment.serialNumber };
                }
            },
            error: (error) => {
                this.toastService.showError(error);
            },
        });
    }

    getCalibrationHistory() {
        const filterBy = JSON.stringify({
            calibrationEquipmentId: this.calibrationId,
        });
        this.isLoading = true;
        this.deviceCalibrationHistoryService
            .get(
                "",
                this.sortActive,
                this.defaultOrderBy,
                this.pageIndex,
                this.pageSize,
                filterBy,
            )
            .subscribe({
                next: (res) => {
                    this.dataSource.data = res.data;
                    this.totalItems = res.count;
                    this.isLoading = false;
                },
                error: (error) => {
                    this.toastService.showError(error);
                    this.isLoading = false;
                },
            });
    }

    onPageChange(event: PageEvent) {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.getCalibrationHistory();
    }

    openUrl(url: string) {
        if (!openFileInNewTab(url)) {
            this.toastService.displayErrorMessage("Certificate URL not found", true);
        }
    }

    onSortChange(sortState: Sort) {
        this.sortActive = sortState.active;
        this.defaultOrderBy = (sortState.direction || "asc") as "asc" | "desc";
        this.getCalibrationHistory();
    }
}
